import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Tag, Button, message, Empty, Segmented, Spin, Space, Popconfirm, List } from 'antd';
import { MedicineBoxOutlined, PrinterOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { pharmacyService } from '../../services';
import PrintPrescription from '../../components/Print/PrintPrescription';

const Prescriptions = () => {
    const { t } = useTranslation();
    const [view, setView] = useState('active'); // 'active' | 'done' | 'all'
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    // The prescription currently being printed (drives the doctor-style printout).
    const [printTarget, setPrintTarget] = useState(null);

    const fetchPrescriptions = useCallback(async () => {
        setLoading(true);
        try {
            const res = await pharmacyService.getPrescriptions(view);
            setItems(res.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    }, [view, t]);

    useEffect(() => {
        fetchPrescriptions();
    }, [fetchPrescriptions]);

    const markDone = async (id) => {
        try {
            await pharmacyService.markPrescriptionDone(id);
            message.success(t('markedDone') || 'Marked as done');
            fetchPrescriptions();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    // Map a pharmacy queue item into the shape PrintPrescription expects, and store
    // it in state. Stable object references (vs. computing inline each render) keep
    // PrintPrescription's auto-print timer from being cancelled by re-renders.
    const openPrint = (p) => {
        setPrintTarget({
            visit: {
                _id: p._id,
                visitDate: p.createdAt,
                prescriptions: p.items || [],
                labTests: [],
                notes: ''
            },
            patient: p.patientId || { name: p.patientName },
            doctorData: p.doctorId || { name: p.doctorName }
        });
    };

    return (
        <div className="page-container">
            <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                <Col><h1 style={{ margin: 0 }}><MedicineBoxOutlined /> {t('prescriptions') || 'Prescriptions'}</h1></Col>
                <Col>
                    <Segmented
                        value={view}
                        onChange={setView}
                        options={[
                            { label: t('active') || 'Active', value: 'active' },
                            { label: t('done') || 'Done', value: 'done' },
                            { label: t('all') || 'All', value: 'all' }
                        ]}
                    />
                </Col>
            </Row>

            <Spin spinning={loading}>
                {items.length === 0 ? (
                    <Empty description={t('noPrescriptions') || 'No prescriptions'} />
                ) : (
                    <Row gutter={[16, 16]}>
                        {items.map((p) => (
                            <Col xs={24} md={12} lg={8} key={p._id}>
                                <Card
                                    title={
                                        <Space direction="vertical" size={0}>
                                            <span>{p.patientId?.name || p.patientName}</span>
                                            <small style={{ color: '#888', fontWeight: 'normal' }}>
                                                {t('byDoctor') || 'Dr.'} {p.doctorId?.name || p.doctorName}
                                            </small>
                                        </Space>
                                    }
                                    extra={p.status === 'done'
                                        ? <Tag color="green">{t('done') || 'Done'}</Tag>
                                        : <Tag color="orange">{t('active') || 'Active'}</Tag>}
                                    actions={[
                                        <Button type="link" icon={<PrinterOutlined />} onClick={() => openPrint(p)} key="print">
                                            {t('print') || 'Print'}
                                        </Button>,
                                        p.status !== 'done' ? (
                                            <Popconfirm
                                                key="done"
                                                title={t('confirmMarkDone') || 'Mark as done?'}
                                                onConfirm={() => markDone(p._id)}
                                            >
                                                <Button type="link" icon={<CheckCircleOutlined />}>
                                                    {t('markDone') || 'Mark Done'}
                                                </Button>
                                            </Popconfirm>
                                        ) : (
                                            <span key="done" style={{ color: '#bbb' }}>
                                                <CheckCircleOutlined /> {t('done') || 'Done'}
                                            </span>
                                        )
                                    ]}
                                >
                                    <List
                                        size="small"
                                        dataSource={p.items || []}
                                        renderItem={(it, i) => (
                                            <List.Item>
                                                <span>
                                                    <strong>{i + 1}. {it.drugName}</strong>
                                                    {' — '}{it.dosage || ''} {it.frequency ? `· ${it.frequency}` : ''} {it.duration ? `· ${it.duration}` : ''}
                                                    {it.notes ? <div style={{ color: '#888' }}>{it.notes}</div> : null}
                                                </span>
                                            </List.Item>
                                        )}
                                    />
                                    <div style={{ marginTop: 8, color: '#aaa', fontSize: 12 }}>
                                        {p.createdAt ? new Date(p.createdAt).toLocaleString() : ''}
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Spin>

            {/* Doctor-style printout — mounted only while printing so the doctor's
                letterhead/doctorInfo is correct from the first render (otherwise the
                auto-print timer gets cancelled and nothing prints). */}
            {printTarget && (
                <PrintPrescription
                    visit={printTarget.visit}
                    patient={printTarget.patient}
                    doctorData={printTarget.doctorData}
                    autoPrint
                    onAfterPrint={() => setPrintTarget(null)}
                />
            )}
        </div>
    );
};

export default Prescriptions;
